
    import { captureMessage } from '@sentry/vue'
    import Vue, { defineComponent, onBeforeUnmount, onMounted, type PropType, readonly, ref } from 'vue'

    import { useI18n } from 'vue-i18n-composable'
    import { ChatMessage as ChatMessageModel } from '@/common/models/data/dashboard/chat/chat-message'

    import ChatMessage from '@/app/components/sidebar/chat/ChatMessage.vue'

    export default defineComponent({
        components: {
            ChatMessage
        },
        props: {
            connecting: {
                type: Boolean,
                required: true
            },
            disabled: {
                type: Boolean,
                default: true
            },
            messages: {
                type: Array as PropType<Array<ChatMessageModel>>,
                required: true
            },
            offline: {
                type: Boolean,
                required: true
            }
        },
        emits: [
            'load-previous'
        ],
        setup (props, { emit }) {
            const i18n = useI18n()
            const element = ref<InstanceType<typeof Vue>>()

            const messageConnecting = readonly(ChatMessageModel.create(
                i18n.t('globals.organization').toString(),
                i18n.t('chat.status.loading').toString()
            ))

            const messageEmpty = readonly(ChatMessageModel.create(
                i18n.t('globals.organization').toString(),
                i18n.t('chat.status.no-messages').toString()
            ))

            const messageOffline = readonly(ChatMessageModel.create(
                i18n.t('globals.organization').toString(),
                i18n.t('chat.status.offline').toString()
            ))

            /**
             * We need to make some calculations, because we're using flex-direction: column-reverse
             * which reverses the values for scrollTop.
             */
            const onScroll = (): void => {
                if (!element.value) {
                    captureMessage('Chat log does not exist', { level: 'warning' })

                    return
                }

                const top = Math.abs(element.value.$el.scrollTop)
                const height = Math.ceil(parseFloat(getComputedStyle(element.value.$el).height))
                const scrollHeight = element.value.$el.scrollHeight

                if (top + height >= scrollHeight) {
                    emit('load-previous')
                }
            }

            const scrollDown = (): void => {
                if (!element.value) {
                    return
                }

                element.value.$el.scrollTop = 0
            }

            onMounted((): void => {
                element.value?.$el.addEventListener('scroll', onScroll, { passive: true })
            })

            onBeforeUnmount((): void => {
                element.value?.$el.removeEventListener('scroll', onScroll)
            })

            return {
                element,
                messageConnecting,
                messageEmpty,
                messageOffline,
                onScroll,
                scrollDown
            }
        }
    })
